import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Card from 'common/components/card';
import CardConfig from 'common/components/card-config';
import CardContent from 'common/components/card-content';
import CardHeader from 'common/components/card-header';
import SvgSymbol from 'common/components/svg-symbol';
import {formatDate} from 'common/formatters/date-formatter';
import {formatNumber} from 'common/formatters/number-formatter';
import {DateTimeFormat} from 'common/proxies/enum-proxy';
import {getIconName} from 'common/utils/resource-icon-util';
import {camelCase} from 'common/utils/string-util';
import {formatNormalizerUnit, formatUnitsJSX} from 'common/utils/units-util';
import {t} from 'common/utils/translation-util';
import ReadoutCardDynamicGauge from './components/readout-card-dynamic-gauge';
import './index.scss';

const NO_VALUE = '--';
const identity = (x) => x;

export default class ReadoutCard extends React.Component {
  static propTypes = {
    comparisonPeriodName: PropTypes.string,
    connectDragSource: PropTypes.func,
    customDecimalPlaces: PropTypes.number,
    dataManagerUrl: PropTypes.string,
    disableConfig: PropTypes.bool,
    dynamicGaugeConfig: PropTypes.shape({
      minimum: PropTypes.number.isRequired,
      maximum: PropTypes.number.isRequired,
      breakpoints: PropTypes.arrayOf(PropTypes.number).isRequired,
      direction: PropTypes.string.isRequired,
    }),
    hasComparison: PropTypes.bool,
    hasDynamicGauge: PropTypes.bool.isRequired,
    hideIcon: PropTypes.bool,
    hideTitle: PropTypes.bool,
    lastReadingTimestamp: PropTypes.string,
    name: PropTypes.string,
    normalizerUnit: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    numericValue: PropTypes.number,
    percentChange: PropTypes.number,
    periodName: PropTypes.string,
    priorityWarnings: PropTypes.array,
    reportId: PropTypes.number,
    resource: PropTypes.string,
    reverseArrowColors: PropTypes.bool,
    // Sign: -1, 0, 1 as string, or null if no comparison
    sign: PropTypes.string,
    units: PropTypes.string,
    warnings: PropTypes.array,
  };

  static defaultProps = {
    disableConfig: false,
  };

  getCardClassName() {
    const classList = ['ReadoutCard-Card'];
    if (this.props.hideTitle) {
      classList.push('use-move-cursor');
    }
    return classList.join(' ');
  }

  getNumericReadoutsClassName() {
    const classList = ['numeric-readouts'];
    if (this.props.hideIcon && !this.props.hideTitle) {
      classList.push('shrink-margin-top');
    }
    return classList.join(' ');
  }

  getPercentChangeContentClassName() {
    const classList = ['percent-change-content'];
    if (this.props.percentChange !== null) {
      classList.push('percent-change-content');
    }
    return classList.join(' ');
  }

  // similar to SummaryFigure#getArrowUrl
  getArrowSvgName() {
    const arrows = {
      consumptionIncrease: 'up',
      consumptionDecrease: 'down',
      productionIncrease: 'up-green',
      productionDecrease: 'down-red',
    };

    if (this.props.sign === null) {
      return '';
    }

    if (this.props.reverseArrowColors) {
      // Upward green arrow for increase, downward red arrow for decrease.
      return this.props.sign < 0
        ? arrows.productionDecrease
        : arrows.productionIncrease;
    }

    // Upward red arrow for increase, downward green arrow for decrease.
    return this.props.sign < 0
      ? arrows.consumptionDecrease
      : arrows.consumptionIncrease;
  }

  render() {
    const rawUnits = this.props.units;
    const isDollars = rawUnits === '$';

    let numericValue;
    if (this.props.numericValue === null) {
      numericValue = NO_VALUE;
    } else if (isDollars) {
      const customFormat = null;
      const currencyCode = 'USD';
      numericValue = formatNumber(
        this.props.numericValue,
        'currency',
        customFormat,
        currencyCode,
        this.props.customDecimalPlaces,
      );
    } else {
      numericValue = formatNumber(
        this.props.numericValue,
        'number',
        null,
        null,
        this.props.customDecimalPlaces,
      );
    }

    let units = this.props.units;
    let normalizerUnit;
    if (this.props.normalizerUnit) {
      normalizerUnit = formatNormalizerUnit(this.props.normalizerUnit);
      units = `${units}${normalizerUnit}`;
    }
    const formattedUnits = formatUnitsJSX(units);

    const percentChange =
      this.props.percentChange === null
        ? NO_VALUE
        : formatNumber(this.props.percentChange);

    const formattedTimestamp =
      this.props.lastReadingTimestamp &&
      formatDate(
        moment.utc(this.props.lastReadingTimestamp),
        DateTimeFormat.TEXTUAL_DAY_OF_WEEK,
      );

    return (
      <div className={'ReadoutCard'}>
        {(this.props.connectDragSource || identity)(
          <div>
            <Card className={this.getCardClassName()} loading={false}>
              {this.props.hideTitle ? (
                <CardConfig
                  className={'ReadoutCard--hideTitle-CardConfig'}
                  disableDownloadCsv={true}
                  reportId={this.props.reportId}
                  style={{
                    width: '14px',
                    height: '14px',
                  }}
                />
              ) : (
                <CardHeader
                  dataManagerUrl={this.props.dataManagerUrl}
                  disableConfig={this.props.disableConfig}
                  disableDownloadCsv={true}
                  priorityWarnings={this.props.priorityWarnings}
                  reportId={this.props.reportId}
                  subtitle={null}
                  title={this.props.name}
                  warnings={this.props.warnings}
                />
              )}

              <CardContent>
                {this.props.hideIcon ? null : (
                  <span
                    className={
                      this.props.hasDynamicGauge
                        ? 'dynamic-gauge'
                        : 'resource-icon'
                    }>
                    {this.props.hasDynamicGauge ? (
                      <ReadoutCardDynamicGauge
                        {...this.props.dynamicGaugeConfig}
                        value={this.props.numericValue}
                      />
                    ) : (
                      <SvgSymbol
                        name={getIconName(camelCase(this.props.resource))}
                        useImg={true}
                      />
                    )}
                  </span>
                )}

                <div className={this.getNumericReadoutsClassName()}>
                  <span className={'numeric-value'}>{numericValue}</span>
                  <span className={'units-readout'}>
                    {isDollars ? normalizerUnit : formattedUnits}
                  </span>
                </div>

                <div className={'lower-readouts'}>
                  {!this.props.hasComparison ? null : (
                    <div className={'percent-change'}>
                      <span className={this.getPercentChangeContentClassName()}>
                        {this.props.percentChange === null ? null : (
                          <SvgSymbol
                            className={'sign-icon'}
                            name={this.getArrowSvgName()}
                            useImg={true}
                          />
                        )}
                        <span className={'percent-value'}>
                          {`${percentChange}%`}
                        </span>
                      </span>
                    </div>
                  )}

                  <div className={'period'}>
                    <span className={'period-readout'}>
                      {formattedTimestamp
                        ? formattedTimestamp
                        : this.props.periodName}
                    </span>

                    {!this.props.hasComparison ? null : (
                      <span>
                        <span className={'compared-to'}>{` ${t(
                          'compared to',
                        )} `}</span>
                        <span
                          className={
                            'period-readout comparison-period-readout'
                          }>
                          {this.props.comparisonPeriodName}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>,
        )}
      </div>
    );
  }
}
